import h from 'hyperscript';
import type { Delta } from 'quill/core';

import { assertNonNullable, assertUnreachable, requireNonNullable } from '@disy/lib/custom-error';
import { vr } from '@disy/ui/separator';
import { getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import { type AttributeWithObjecttype, attributeObjecttypeLabel } from 'ui/attribute-label/attribute-formatting-utils';
import { createAttributeLabel } from 'ui/attribute-label/attribute-label';

import type { ExternalLayerDescriptions } from 'cadenza/api-client/workbook-layer/workbook-layer-capabilities-dto';
import { getLayerSetting } from 'cadenza/map/context/workbook/layer-context-util';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { getStore } from 'cadenza/store/cadenza-store';
import { type LayerLabelType, createLayerLabel } from 'cadenza/ui/layer-label/layer-label';
import { createVisualizationTypeLabel } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { isMap } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import { dataTypeLabel } from 'cadenza/workbook/attribute-element/data-type-icon';
import { getAllAttributeIdsFromDataView } from 'cadenza/workbook/data-view/data-view-utils';
import { getWorkbookLayerType } from 'cadenza/workbook/map/workbook-layer-type-util';
import { type AttributeBundleName, UNUSED_BUNDLE_NAME } from 'cadenza/workbook/model/attribute-bundle-name';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key.gen';
import {
  getAttributeById,
  getAttributeDescriptionVisibility,
  getDataViewById,
  getLayerDataByLayerId,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';

export function renderLayerSummary(layerId: LayerId, externalDescriptions?: ExternalLayerDescriptions) {
  const layerData = requireNonNullable(getLayerDataByLayerId(getStore().getState(), layerId));
  return layerData.dataView ?
      renderDataViewSummary(layerData.dataView.id)
    : renderNonDataViewSummary(layerData.definition, externalDescriptions);
}

function createAttributeDetail(attribute: WorkbookAttributeDto) {
  const attributeDetailContainer = h(
    `.d-view-info--attribute.d-stack-v.space-1`,
    createAttributeLabel(attribute.type, attribute.printName),
  );
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    attributeDetailContainer.append(createAttributeDescription(attribute));
  }
  if (attribute.description) {
    attributeDetailContainer.append(
      h('d-truncated-p', {
        fullText: attribute.description,
      }),
    );
  }
  return attributeDetailContainer;
}

function createAttributesList(dataView: DataViewDto) {
  return getAllAttributeIdsFromDataView(dataView)
    .map((attrId) => getAttributeById(getStore().getState(), attrId))
    .flatMap((attribute) => createAttributeDetail(attribute));
}

export function hasAtLeastOneUsedAttribute(dataView: DataViewDto) {
  return !!Object.keys(dataView.attributes)
    ?.filter((bundleName) => bundleName !== UNUSED_BUNDLE_NAME)
    .flatMap((bundleName) => dataView.attributes[bundleName as AttributeBundleName]).length;
}

export function shouldShowAttributeSummary(view: ViewDto) {
  if (view.type === 'IMAGE') {
    return false;
  }
  const visibilityConfiguration = getAttributeDescriptionVisibility(getStore().getState());
  switch (visibilityConfiguration) {
    case 'ALWAYS_VISIBLE':
      return true;
    case 'NEVER_VISIBLE':
      return false;
    case 'USER_CONFIGURABLE':
      return (
        getSettingOrSettingDefault(view, CommonViewSettingsKey.toPersistenceKey('DESCRIPTION_SHOW_ATTRIBUTES')) &&
        view.dataViews
          .map((dataViewId) => getDataViewById(getStore().getState(), dataViewId))
          .some((dataView) => hasAtLeastOneUsedAttribute(dataView))
      );
    default:
      assertUnreachable(visibilityConfiguration);
  }
}

function createAttributeDescription(attribute: WorkbookAttributeDto) {
  return h('.attribute-description.d-stack-h.space-2.text-muted', [
    h('span', AttributeCategory.i18n(attribute.category, { count: 1 })),
    vr(),
    h('.attribute-description--datatype', dataTypeLabel(attribute)),
    vr(),
    h('.attribute-description--objecttype-name', [attributeObjecttypeLabel(attribute as AttributeWithObjecttype)]),
  ]);
}

export function renderDataViewSummary(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  const description = getSetting<string | Delta>(dataView, CommonDataViewSettingsKey.toPersistenceKey('DESCRIPTION'));
  const showDescription =
    getSetting(dataView, MapDataViewSettingsKey.toPersistenceKey('INFO_WINDOW_DESCRIPTION'), false) && !!description;
  const showAttributes =
    getSetting(dataView, MapDataViewSettingsKey.toPersistenceKey('INFO_WINDOW_ATTRIBUTE'), false) &&
    hasAtLeastOneUsedAttribute(dataView);
  if (showDescription || showAttributes) {
    const label =
      isMap(dataView.visualizationKey) ?
        createLayerLabel(dataView.visualizationKey, dataView.name)
      : createVisualizationTypeLabel(dataView.visualizationKey, dataView.name);
    const elements: HTMLElement[] = [h('h2', label)];
    if (showDescription) {
      if (typeof description === 'string') {
        elements.push(h('d-truncated-p', { fullText: description }));
      } else {
        const quillContainer = h('div');
        elements.push(quillContainer);
        import('cadenza/quill/quill-content-utilities').then(({ getQuillContent }) =>
          getQuillContent(description, dataViewId, quillContainer),
        );
      }
    }
    if (showAttributes) {
      elements.push(...createAttributesList(dataView));
    }
    return h('.d-stack-v.space-2', ...elements);
  }
}

function renderNonDataViewSummary(layer: WorkbookLayerDto, externalDescription?: ExternalLayerDescriptions) {
  const layerLabelType = getLayerLabelType(layer);
  const layerName = getLayerSetting<string>(layer, 'LAYER_NAME');
  assertNonNullable(layerName, `Missing name for for layer ${layer.id}`);
  const description = getLayerSetting<string>(layer, 'DESCRIPTION');
  const showDescription = getLayerSetting<boolean>(layer, 'INFO_WINDOW_SHOW_DESCRIPTION');
  const externalLayerDescription = !description && externalDescription && externalDescription[layer.id]?.defaultValue;
  if (!showDescription || (!description && !externalLayerDescription)) {
    return;
  }
  return h('.d-stack-v.space-2', [
    h('h1', createLayerLabel(layerLabelType, layerName)),
    h('d-truncated-p', {
      fullText: externalLayerDescription || description,
    }),
  ]);
}

function getLayerLabelType(layer: WorkbookLayerDto): LayerLabelType {
  const workbookLayerType = getWorkbookLayerType(layer);
  switch (workbookLayerType) {
    case 'STANDARD_GROUP':
    case 'TOGGLE_GROUP':
      return 'group';
    case 'DATA_VIEW':
      return 'data-view';
    case 'SKETCH':
      return 'sketch';
    case 'EXTERNAL_ARCGIS_MAP':
    case 'EXTERNAL_ARCGIS_IMAGE':
    case 'EXTERNAL_WMS':
    case 'EXTERNAL_WMTS':
    case 'EXTERNAL_VECTOR_TILE':
      return 'external';
    case 'CLASSIC':
      return 'data-view'; // In this case the type is used for a layer icon, it is ok to display the data view icon for classic layers.
    default:
      assertUnreachable(workbookLayerType);
  }
}
